import { useEffect } from "react"
import clsx from "clsx"

import Slider, { useSliderState } from "@unlikelystudio/react-slider"

import type { PropsWithClassName } from "~/@types/generics"
import { Image, type ImageProps } from "~/components/ui/Image"
import type { TProductHeader } from "~/components/ui/ProductHeader/_data/serializer"
import { isVideo, type ProductMediasProps } from "~/components/ui/ProductHeader/components/ProductMedias"
import Video from "~/components/ui/Video"
import Icon from "~/components/abstracts/Icon"
import { Ratio } from "~/components/abstracts/Ratio"

import { sprinkles } from "~/styles/sprinkles.css"

import * as css from "./styles.css"

const NAV_IMAGE_SIZES = [{ ratio: 1 / 15 }]

export type DesktopNavigationProps = PropsWithClassName<{
  onClick?(index: number): void
  activeItems: number[]
  items: TProductHeader["serializedProduct"]["medias"]
}>

function DesktopNavigation({ className, items, activeItems, onClick }: DesktopNavigationProps) {
  const [{ setSlideIndex, slideIndex }, setNavSliderState] = useSliderState()

  useEffect(() => {
    activeItems[0] !== undefined && setSlideIndex(activeItems[0])
  }, [activeItems])

  const NavigationItems = (media: ProductMediasProps["items"][number], key: number) => {
    const props = {
      imageClassName: clsx(css.navigationImage),
      sizesFromBreakpoints: NAV_IMAGE_SIZES,
      asPlaceholder: true,
    } satisfies Partial<ImageProps>

    return (
      <button
        key={`desktop_navigation_item_${key}`}
        className={clsx(css.NavigationItem({ active: activeItems.indexOf(key) !== -1 }))}
        onClick={() => onClick?.(key)}
        aria-label={key.toString()}
      >
        {!isVideo(media) ? (
          <Image {...props} {...media} />
        ) : (
          <Ratio
            styleContainer={{
              paddingBottom: `${(Number(media.sources[0]?.height ?? 1) / Number(media.sources[0]?.width ?? 0)) * 100}%`,
            }}
          >
            <Video className={clsx(css.video)} {...media} />
          </Ratio>
        )}
      </button>
    )
  }

  return (
    <div className={clsx(css.DesktopMediasNavigationWrapper, className)}>
      {slideIndex > 0 ? (
        <button
          className={sprinkles({ p: 4 })}
          onClick={() => {
            onClick?.(slideIndex - 1)
          }}
        >
          <Icon name="Arrow" width={12} direction="top" />
        </button>
      ) : (
        <div className={css.placeholder} />
      )}
      <nav>
        <Slider
          className={clsx(css.DesktopMediasNavigationSlider)}
          setSliderState={setNavSliderState}
          snap
          axis="y"
          autoBlockSlideIndexChange={true}
          style={{ overflow: "hidden" }}
        >
          {items.map(NavigationItems)}
        </Slider>
      </nav>
      {slideIndex < items.length - 1 ? (
        <button
          className={sprinkles({ p: 4 })}
          onClick={() => {
            onClick?.(slideIndex + 1)
          }}
        >
          <Icon name="Arrow" width={12} direction="bottom" />
        </button>
      ) : (
        <div className={css.placeholder} />
      )}
    </div>
  )
}

export default DesktopNavigation
